import { useCallback, useEffect, useState } from 'react';

import {
  type ModelChoices,
  effectiveModel,
  fetchModelChoices,
  loadStoredModel,
  modelForRequest,
  saveStoredModel,
} from './models';

export interface UseModelChoice {
  /** Offered models, or null while loading / on error (the control is hidden). */
  choices: ModelChoices | null;
  /** The selected option key (the default until the user picks). */
  selected: string | null;
  select: (key: string) => void;
  /** What to send as `model` on the next chat request; undefined = send nothing. */
  requestModel: string | undefined;
}

/** The global model choice: options from the Gateway, the user's pick from localStorage. */
export function useModelChoice(): UseModelChoice {
  const [choices, setChoices] = useState<ModelChoices | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchModelChoices()
      .then((c) => {
        if (cancelled || !c) return;
        setChoices(c);
        setSelected(effectiveModel(c, loadStoredModel()));
      })
      .catch((err) => {
        // No dropdown and no `model` sent: the runtime default applies.
        if (!cancelled) console.warn('[useModelChoice] model options unavailable', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const select = useCallback((key: string) => {
    setSelected(key);
    saveStoredModel(key);
  }, []);

  return { choices, selected, select, requestModel: modelForRequest(choices, selected) };
}
